import { richContentBlocks } from './richText';
import { safeMessageUrl } from './messageUrl';
export { safeMessageUrl } from './messageUrl';

/** One safe allowlist for imported messages and authored comments. */
export type MarkupFormat = 'plain' | 'markdown' | 'chat' | 'rich';
export interface MessageText {
  text: string;
  format: MarkupFormat;
  /** Versioned editor JSON, normalized before rendering. Never interpreted as HTML. */
  document?: unknown;
}
export interface MessageEntity {
  id: string;
  kind: 'user' | 'channel';
  name: string;
}
export type InlineContent =
  | { kind: 'text' | 'code'; text: string }
  | { kind: 'strong' | 'emphasis' | 'strike' | 'underline'; children: InlineContent[] }
  | { kind: 'link'; href: string; children: InlineContent[] }
  | { kind: 'entity'; entity: MessageEntity };
export type ContentBlock =
  | { kind: 'paragraph'; children: InlineContent[] }
  | { kind: 'quote'; children: InlineContent[]; blocks?: ContentBlock[] }
  | { kind: 'code'; text: string; language: string }
  | { kind: 'list'; ordered: boolean; start: number; items: ContentBlock[][] };

const emoji: Readonly<Record<string, string>> = {
  wave: '👋',
  sparkles: '✨',
  white_check_mark: '✅',
  thumbsup: '👍',
  eyes: '👀',
  smile: '😄',
  rocket: '🚀',
  heart: '❤️',
  tada: '🎉',
};

function inline(
  text: string,
  format: MarkupFormat,
  entities: readonly MessageEntity[],
  depth = 0,
  inLink = false,
): InlineContent[] {
  if (format === 'plain' || depth >= 8) return [{ kind: 'text', text }];
  const result: InlineContent[] = [];
  const pushText = (value: string) => {
    const previous = result.at(-1);
    if (previous?.kind === 'text') previous.text += value;
    else result.push({ kind: 'text', text: value });
  };
  const marks: [string, 'strong' | 'emphasis' | 'strike'][] =
    format === 'chat'
      ? [
          ['*', 'strong'],
          ['_', 'emphasis'],
          ['~', 'strike'],
        ]
      : [
          ['**', 'strong'],
          ['__', 'strong'],
          ['~~', 'strike'],
          ['*', 'emphasis'],
          ['_', 'emphasis'],
        ];
  let i = 0;
  while (i < text.length) {
    const rest = text.slice(i);
    if (text[i] === '\\' && i + 1 < text.length && /[\\*_~\x60<>[\]:!]/u.test(text[i + 1])) {
      pushText(text[i + 1]);
      i += 2;
      continue;
    }
    if (text[i] === '\x60') {
      const end = text.indexOf('\x60', i + 1);
      if (end > i + 1 && !text.slice(i + 1, end).includes('\n')) {
        result.push({ kind: 'code', text: text.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }
    if (!inLink) {
      // Images are deliberately preserved as text; external media never loads during rendering.
      const link = rest.match(/^(!?)\[([^\]\n]{1,2000})\]\(([^)\n]{1,2048})\)/u);
      if (link) {
        const href = link[1] ? null : safeMessageUrl(link[3]);
        if (href)
          result.push({ kind: 'link', href, children: inline(link[2], format, entities, depth + 1, true) });
        else pushText(link[0]);
        i += link[0].length;
        continue;
      }
    }
    if (text[i] === '<') {
      const external = rest.match(/^<([^<>\n]{1,2048})>/u);
      if (external) {
        const token = external[1];
        const mention = token.match(/^([@#])([^|]+)(?:\|[^|]+)?$/u);
        const entity =
          mention &&
          entities.find((e) => e.id === mention[2] && e.kind === (mention[1] === '@' ? 'user' : 'channel'));
        const [url, ...label] = token.split('|');
        const href = inLink ? null : safeMessageUrl(url);
        if (entity && !inLink) result.push({ kind: 'entity', entity });
        else if (href)
          result.push({ kind: 'link', href, children: [{ kind: 'text', text: label.join('|') || url }] });
        else pushText(external[0]);
        i += external[0].length;
        continue;
      }
    }
    const alias = text[i] === ':' && rest.match(/^:([a-z_]{1,40}):/u);
    if (alias && Object.hasOwn(emoji, alias[1])) {
      pushText(emoji[alias[1]]);
      i += alias[0].length;
      continue;
    }
    let matched = false;
    for (const [mark, kind] of marks) {
      if (!rest.startsWith(mark)) continue;
      // Delimiters in words (e.g. snake_case) remain literal.
      if (mark === '_' && i > 0 && /[\p{L}\p{N}]/u.test(text[i - 1])) continue;
      const end = text.indexOf(mark, i + mark.length);
      if (end <= i + mark.length) continue;
      const value = text.slice(i + mark.length, end);
      if (/^\s|\s$/u.test(value)) continue;
      result.push({ kind, children: inline(value, format, entities, depth + 1, inLink) });
      i = end + mark.length;
      matched = true;
      break;
    }
    if (matched) continue;
    pushText(text[i]);
    i++;
  }
  return result;
}

export function parseMessageContent(
  source: MessageText,
  entities: readonly MessageEntity[] = [],
): ContentBlock[] {
  if (source.format === 'rich')
    return (
      richContentBlocks(source.document, entities) ?? [
        { kind: 'paragraph', children: [{ kind: 'text', text: source.text }] },
      ]
    );
  if (source.format === 'plain' || source.text.length > 100000)
    return [{ kind: 'paragraph', children: [{ kind: 'text', text: source.text }] }];
  const lines = source.text.replace(/\r\n?/gu, '\n').split('\n');
  const blocks: ContentBlock[] = [];
  let paragraph: string[] = [];
  const flush = () => {
    if (paragraph.length)
      blocks.push({ kind: 'paragraph', children: inline(paragraph.join('\n'), source.format, entities) });
    paragraph = [];
  };
  for (let i = 0; i < lines.length; i++) {
    const fence = lines[i].match(/^\x60{3}([a-zA-Z0-9+#.-]*)\s*$/u);
    if (fence) {
      let end = i + 1;
      while (end < lines.length && !/^\x60{3}\s*$/u.test(lines[end])) end++;
      if (end < lines.length) {
        flush();
        blocks.push({
          kind: 'code',
          language: fence[1].toLowerCase(),
          text: lines.slice(i + 1, end).join('\n'),
        });
        i = end;
        continue;
      }
      // An unclosed fence stays literal; scan the remainder only once.
      flush();
      blocks.push({ kind: 'paragraph', children: [{ kind: 'text', text: lines.slice(i).join('\n') }] });
      break;
    }
    if (lines[i].startsWith('> ')) {
      flush();
      const quote = [lines[i].slice(2)];
      while (lines[i + 1]?.startsWith('> ')) quote.push(lines[++i].slice(2));
      blocks.push({ kind: 'quote', children: inline(quote.join('\n'), source.format, entities) });
    } else if (!lines[i].trim()) flush();
    else paragraph.push(lines[i]);
  }
  flush();
  return blocks;
}
